import React, { useState, useEffect, useCallback } from 'react';
import { Button, Container, Text } from 'components';
import { dataTrackingOperations, hooks, mainOperations } from 'state';
import { FiMail, FiUsers } from 'react-icons/fi';

import EdgeSubscriptionForm from './EdgeSubscriptionForm';
import EdgeSubscriptionsList from './EdgeSubscriptionsList';
import type { EdgeSubscriptionData, EdgeSubscriptionFormData } from './types';
import { extractListFromResponse, mapApiItemToEdgeSubscription, mapFormDataToPutPayload, mapTerritoriesToOptions } from './utils';
import { getUserFromSubcookies } from 'utils/tools';
import { isVMiQ } from 'utils/constants';
import './edge-subscriptions-manager.css';

export type { EdgeSubscriptionData, EdgeSubscriptionFormData } from './types';

interface EdgeSubscriptionsManagerProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'new' | 'tracking';
  onTabChange?: (tab: 'new' | 'tracking') => void;
}

const EdgeSubscriptionsManager: React.FC<EdgeSubscriptionsManagerProps> = ({
  isOpen,
  onClose,
  initialTab = 'new',
  onTabChange,
}) => {
  const [activeTab, setActiveTab] = useState<'new' | 'tracking'>(initialTab);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingSubscriptions, setIsFetchingSubscriptions] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showDeleteMessage, setShowDeleteMessage] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [subscriptions, setSubscriptions] = useState<EdgeSubscriptionData[]>([]);

  const { useAppDispatch } = hooks;
  const { trackEvent } = dataTrackingOperations;
  const { getEdgeSubscriptions, createEdgeSubscription, deleteEdgeSubscription, updateEdgeSubscriptionIsActive, getTerritories } = mainOperations;
  const dispatch = useAppDispatch();

  const fetchSubscriptions = useCallback(async () => {
    if (!isOpen) return;
    setIsFetchingSubscriptions(true);
    try {
      const [subsResult, terrResult] = await Promise.all([
        dispatch(getEdgeSubscriptions()),
        dispatch(getTerritories()),
      ]);
      const subsPayload = subsResult.payload as { edgeSubscriptions?: unknown } | undefined;
      const terrPayload = terrResult.payload as { territories?: unknown } | undefined;
      const territoryOptions = mapTerritoriesToOptions(terrPayload?.territories);
      const territoryMap = Object.fromEntries(territoryOptions.map(t => [t.value, t.label]));

      const rawList = extractListFromResponse(subsPayload?.edgeSubscriptions);
      const mapped = rawList
        .filter((x): x is Record<string, unknown> => x != null && typeof x === 'object')
        .filter(x => x.isDeleted !== true)
        .map(item => {
          const sub = mapApiItemToEdgeSubscription(item);
          // Si el territory es un GUID (no tiene espacios ni nombre legible), resolverlo
          if (sub.territory && territoryMap[sub.territory]) {
            return { ...sub, territory: territoryMap[sub.territory] };
          }
          return sub;
        })
        .filter(sub => sub.id);
      setSubscriptions(mapped);
    } catch (err) {
      console.warn('[EdgeSubscriptions] API error:', err);
      setSubscriptions([]);
    } finally {
      setIsFetchingSubscriptions(false);
    }
  }, [isOpen, dispatch, getEdgeSubscriptions, getTerritories]);

  useEffect(() => {
    if (isOpen) {
      fetchSubscriptions();
    }
  }, [isOpen, fetchSubscriptions]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      onTabChange?.(initialTab);
    }
  }, [isOpen, initialTab]);

  const handleToggleActive = async (id: string) => {
    const sub = subscriptions.find(s => s.id === id);
    if (!sub) return;
    const newIsActive = !sub.isActive;
    try {
      await dispatch(updateEdgeSubscriptionIsActive({ edgeGuid: id, isActive: newIsActive })).unwrap();
      setSubscriptions(prev =>
        prev.map(s => (s.id === id ? { ...s, isActive: newIsActive } : s))
      );
    } catch (error) {
      console.error('Error updating subscription status:', error);
    }
  };

  const handleDeleteSubscription = async (id: string) => {
    try {
      await dispatch(deleteEdgeSubscription(id)).unwrap();
      setSubscriptions(prev => prev.filter(sub => sub.id !== id));
      setShowDeleteMessage(true);
      setTimeout(() => setShowDeleteMessage(false), 3000);
    } catch (error) {
      console.error('Error deleting subscription:', error);
    }
  };

  const handleSaveSubscription = async (formData: EdgeSubscriptionFormData) => {
    setIsLoading(true);
    setSaveError(null);
    try {
      const userGuid = getUserFromSubcookies()?.profile?.sub ?? '';
      const payload = mapFormDataToPutPayload(formData, userGuid);
      const result = await dispatch(createEdgeSubscription(payload)).unwrap();
      const created = result?.edgeSubscription;
      if (created && typeof created === 'object') {
        const mapped = mapApiItemToEdgeSubscription(created as Record<string, unknown>);
        if (mapped.id) {
          setSubscriptions(prev => [mapped, ...prev]);
        } else {
          fetchSubscriptions();
        }
      } else {
        fetchSubscriptions();
      }
      await dispatch(trackEvent({
        eventCategory: 'edge_subscription',
        event: 'subscription_created',
        values: { edgeType: formData.edgeType, periodicity: formData.periodicity }
      }));
      setActiveTab('tracking');
      onTabChange?.('tracking');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error: any) {
      const code = error?.errorCode ?? error?.code ?? '';
      if (code === 'InvalidVmiqBrand' || String(error?.message ?? '').includes('InvalidVmiqBrand')) {
        setSaveError('The selected brand is not available for Inventory Edge in this VMiQ account. Please choose a different brand.');
      } else {
        setSaveError('An error occurred while saving the subscription. Please try again.');
      }
      console.error('Error saving subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (tab: 'new' | 'tracking') => {
    setActiveTab(tab);
    onTabChange?.(tab);
    dispatch(trackEvent({
      eventCategory: 'edge_subscription',
      event: 'tab_changed',
      values: { tab }
    }));
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={`edge-subscriptions-manager-content${isVMiQ ? ' product-vmiq' : ''}`}>
        <div className="edge-subscriptions-tabs">
          <Button
            color={activeTab === 'new' ? 'brand' : undefined}
            onClick={() => handleTabChange('new')}
            className={`tab-button ${activeTab === 'new' ? 'primary' : 'secondary'}`}
          >
            <FiMail size={16} />
            <Text textStyle="button">New Subscription</Text>
          </Button>
          <Button
            color={activeTab === 'tracking' ? 'brand' : undefined}
            onClick={() => handleTabChange('tracking')}
            className={`tab-button ${activeTab === 'tracking' ? 'primary' : 'secondary'}`}
          >
            <FiUsers size={16} />
            <Text textStyle="button">{`Manage Subscriptions (${subscriptions.length})`}</Text>
          </Button>
        </div>

        <div className="edge-subscriptions-content">
          {activeTab === 'new' ? (
            <EdgeSubscriptionForm
              onSubmit={handleSaveSubscription}
              isLoading={isLoading}
              existingNames={subscriptions.map(s => s.subscriptionName).filter(Boolean)}
            />
          ) : (
            <EdgeSubscriptionsList
              subscriptions={subscriptions}
              onToggleActive={handleToggleActive}
              onDelete={handleDeleteSubscription}
              isFetching={isFetchingSubscriptions}
            />
          )}
        </div>
      </div>

      {showSuccessMessage && (
        <div className="success-message">
          ✅ Subscription saved successfully!
        </div>
      )}
      {saveError && (
        <div className="error-message" style={{ marginTop: 8 }}>
          ❌ {saveError}
        </div>
      )}
      {showDeleteMessage && (
        <div className="success-message">
          ✅ Subscription deleted successfully!
        </div>
      )}
    </>
  );
};

export default EdgeSubscriptionsManager;
